import React, { useRef } from 'react';
import type { Corner } from '../../app/extension-ui/shared/types';
import SVGIcon from '../../components/SVGIcon';
import resizeHandlerIcon from '../../icons/prefilled/extension/resize-handler.svg';
import * as styles from './PostModalViewResizeButton.module.scss';

interface PostModalViewResizeButtonProps {
  corner: Corner;
  onResizeStart?: (position: [number, number], corner: Corner) => void;
  onResizeMove?: (position: [number, number], corner: Corner) => void;
  onResizeEnd?: () => void;
}

const PostModalViewResizeButton = ({
  corner,
  onResizeStart,
  onResizeMove,
  onResizeEnd,
}: PostModalViewResizeButtonProps) => {
  const resizingStateRef = useRef<{ capturedElement: Element } | null>(null);

  if (!onResizeStart || !onResizeMove || !onResizeEnd) {
    return null;
  }

  const handleStartResizing = (event: React.PointerEvent) => {
    if (!(event.target instanceof Element)) return;

    resizingStateRef.current = { capturedElement: event.target };
    event.target.setPointerCapture(event.pointerId);
    onResizeStart([event.screenX, event.screenY], corner);
  };

  const handleEndResizing = (event: React.PointerEvent) => {
    if (!resizingStateRef.current) return;

    resizingStateRef.current.capturedElement.releasePointerCapture(
      event.pointerId,
    );
    resizingStateRef.current = null;
    onResizeEnd();
  };

  const handleResizing = (event: React.PointerEvent) => {
    if (!resizingStateRef.current) return;

    onResizeMove([event.screenX, event.screenY], corner);
  };

  return (
    <button
      type="button"
      aria-label="Resize post"
      className={styles.resizeHandler}
      data-corner={corner}
      onPointerDown={handleStartResizing}
      onPointerUp={handleEndResizing}
      onPointerLeave={handleEndResizing}
      onPointerMove={handleResizing}
    >
      {corner === 'bottom-right' && <SVGIcon>{resizeHandlerIcon}</SVGIcon>}
    </button>
  );
};

export default PostModalViewResizeButton;
